import { MARGIN_LIST } from '@comm_service/constants';
import commService from '@comm_service/index';
import {
  API_BASE,
  EVENT_DELIVERY_COUNTRY_CHANGE,
  EVENT_DELIVERY_METHOD_CHANGE,
  EVENT_HIDE_MESSAGE,
  EVENT_UPDATE_CART_ITEM,
  INVENTORY_TYPE_RESALE,
  PAGE_IDENTIFIER,
  ROKT_SANDBOX_WIDGET_MODE,
} from '@rokt/constants/sdk';
import buildInitResponse from '@rokt/helpers/buildInitResponse';
import roktLoader from '@rokt/roktloader/index';
import { RoktAttributes, roktPayload } from '@rokt/types/rokt';
import { SDKMode, SDKProps } from '@rokt/types/sdk';
import { UpsellCartData } from '@rokt/types/upsellCartData';
import Exception from '@shared/errors/exception';
import { dispatchException } from '@shared/errors/exceptionHandler';
import { Callbacks, InboundEvents, Margins, PLACEMENTS_CALLBACKS } from '@shared/types/commService';
import { findGetParameter } from '@shared/utils';
import { SDKAttributes } from 'types/sdk';

import { loadSponsorshipUI } from '../sponsorship_ui';

declare global {
  interface Window {
    roktService: any;
    sponsorship: any;
    SponsParkwhizWidget: SponsParkwhizWidget | any;
    loadRoktSDK: (...args: any[]) => any;
  }
}

class SponsParkwhizWidget {
  boundRegisterCallbacks: (...args: any[]) => any;
  globalPageIdentifier = '';

  constructor() {
    this.boundRegisterCallbacks = this.registerCallbacks.bind(this);
    commService.handleRegisterCallbacks = this.boundRegisterCallbacks;
    commService.setTargetOrigin(findGetParameter(location.search, 'targetOrigin'));
    this.loaded(true);
  }

  private isRoktServiceAvailable(): boolean {
    return typeof window !== 'undefined' && !!window.roktService;
  }

  async init(sdkProps: SDKProps) {
    this._iframeBodyMarginsManager(sdkProps.component as string, sdkProps.pageIdentifier as string);

    if (sdkProps.onUpdatePresent) sdkProps.onUpdateItem = this.onUpdateItem;
    if (sdkProps.onFanaticsDeliveryUpdatePresent)
      sdkProps.onFanaticsDeliveryUpdate = this.onFanaticsDeliveryUpdate;
    if (sdkProps.onReadMoreCallbackPresent) sdkProps.onReadMoreCallback = this.onReadMoreCallback;
    if (sdkProps.onOptInToPartnerValidationPresent)
      sdkProps.onOptInToPartnerValidation = this.onOptInToPartnerValidation;

    await this._launchRokt(sdkProps);

    return buildInitResponse(true);
  }

  sponsorshipInitialized(sdkProps: SDKProps, cartData: UpsellCartData): void {
    loadSponsorshipUI(sdkProps, cartData);
    this.roktInitialized(true);
  }

  onUpdateItem(value: any): void {
    commService.routeEventMessage({
      data: {
        event: InboundEvents.PARKWHIZ_SDK_ON_UPDATE_ITEM_SUBSCRIPTION,
        message: value,
      },
    });
  }

  onFanaticsDeliveryUpdate(value: any) {
    commService.routeEventMessage({
      data: {
        event: InboundEvents.FANATICS_DELIVERY_DETAILS_SENT,
        message: value,
      },
    });
  }

  onReadMoreCallback(value: any) {
    commService.routeEventMessage({
      data: {
        event: InboundEvents.PARKWHIZ_SDK_ON_READ_MORE,
        message: value,
      },
    });
  }

  onOptInToPartnerValidation(value: any) {
    commService.routeEventMessage({
      data: {
        event: InboundEvents.PARKWHIZ_SDK_ON_OPT_IN_TO_PARTNER_VALIDATION,
        message: value,
      },
    });
  }

  loaded(value: boolean): void {
    commService.routeEventMessage({
      data: {
        event: InboundEvents.PARKWHIZ_SDK_IFRAME_LOADED,
        message: value,
      },
    });
  }

  roktInitialized(value: boolean): void {
    commService.routeEventMessage({
      data: {
        event: InboundEvents.PARKWHIZ_SDK_INITIALIZED,
        message: value,
      },
    });
  }

  registerCallbacks() {
    if (!window.roktService) {
      console.log('No roktService found, skipping callback registration');
      return;
    }
    try {
      window.roktService.cart.onUpdateItem((updatedItem: any, oldItem: any) => {
        commService.routeEventMessage({
          data: {
            event: InboundEvents.PARKWHIZ_SDK_ON_UPDATE_ITEM,
            message: { updatedItem, oldItem },
          },
        });
      });
      window.roktService.onFailure((reason: string) => {
        commService.routeEventMessage({
          data: {
            event: InboundEvents.PARKWHIZ_SDK_ON_FAILURE,
            message: reason,
          },
        });
      });
    } catch (err: any) {
      dispatchException('303', err.message);
    }
  }

  deliveryMethodChange(params: any) {
    this.buildMessage(EVENT_DELIVERY_METHOD_CHANGE, params);
  }

  deliveryCountryChange(params: any) {
    this.buildMessage(EVENT_DELIVERY_COUNTRY_CHANGE, params);
  }

  hideMessage() {
    this.buildMessage(EVENT_HIDE_MESSAGE);
  }

  updateCartItem(params: any) {
    if (this.isRoktServiceAvailable()) {
      window.roktService.getPlacements().then(this.sendMessage(EVENT_UPDATE_CART_ITEM, params));
      return;
    }

    if (typeof window !== 'undefined' && window.sponsorship) {
      window.sponsorship.updateCartItemQuantity(params);
      return;
    }

    console.warn('Neither roktService nor sponsorship is available, cannot update cart item');
  }

  buildMessage(messageType: string, params: any = {}) {
    const message = {
      id: `${this.globalPageIdentifier}_alert`,
      ...params,
    };

    if (this.isRoktServiceAvailable()) {
      window.roktService.getPlacements().then(this.sendMessage(messageType, message));
      return;
    }

    console.warn(`roktService is not available, cannot send message: ${messageType}`);
  }

  sendMessage(eventType: string, message: any) {
    return (placements: any) => {
      if (placements?.length) {
        placements.forEach((placement: any) => {
          placement.send(eventType, message).then(() => {
            console.debug(`${eventType} received: ${JSON.stringify(message)}`);
          });
        });
      }
    };
  }

  isROKTSessionIDAvailable(upsellCartData: UpsellCartData): boolean {
    const sessionId =
      upsellCartData.roktUpsells?.sessionId || upsellCartData.parking?.rokt?.sessionId;
    return !!sessionId;
  }

  private async _launchRokt(sdkProps: SDKProps) {
    const {
      attributes,
      component,
      onUpdatePresent,
      pageIdentifier = PAGE_IDENTIFIER,
      roktTagId,
      sponsId,
      noDeviceId,
    } = sdkProps;
    this.globalPageIdentifier = pageIdentifier;

    try {
      const upsellCartData = !sponsId
        ? { roktTagId }
        : await this._getCustomerCartData(sponsId, component);

      if (
        sdkProps.mode !== SDKMode.EPHEMERAL &&
        !this.isROKTSessionIDAvailable(upsellCartData) &&
        !sponsId
      ) {
        dispatchException('202');
      }

      if (upsellCartData?.roktTagId && attributes) {
        roktLoader(upsellCartData.roktTagId)
          .then(() => {
            if (!window.roktService) dispatchException('300');
            window.roktService.onLoaded(() => {
              this._beforeInit(
                attributes,
                component,
                upsellCartData,
                pageIdentifier,
                onUpdatePresent,
                noDeviceId
              );
            });
            window.roktService.onLoaded(() => {
              this.roktInitialized(true);
            });
          })
          .catch(() => {
            dispatchException('301');
          });
      } else if (upsellCartData && upsellCartData.roktTagId === undefined && attributes) {
        this.sponsorshipInitialized(sdkProps, upsellCartData);
        this.roktInitialized(true);
        return true;
      } else {
        this.roktInitialized(false);
      }

      const roktIsReady: boolean = window.roktService && upsellCartData;
      return roktIsReady;
    } catch (err: any) {
      if (err instanceof Exception) throw err;
      else dispatchException('309', err.message);
    }
  }

  private _beforeInit(
    attributes: SDKAttributes,
    component: string | undefined,
    upsellCartData: UpsellCartData,
    pageIdentifier: string,
    onUpdatePresent = false,
    noDeviceId = false
  ): void {
    try {
      const roktInitConf = this._buildRoktInitConfig(
        upsellCartData,
        component,
        noDeviceId,
        pageIdentifier
      );
      window.roktService.init(roktInitConf);
      const roktAttr: roktPayload = this._buildRoktPayload(upsellCartData, attributes);
      window.roktService.setAttributes(roktAttr);
      window.roktService.triggerPageChange({ pageIdentifier: pageIdentifier });
      this._setGA();

      if (onUpdatePresent) {
        this.getPlacements();
      }
    } catch (err: any) {
      dispatchException('302', err.message);
    }
  }

  async getPlacements() {
    if (this.isRoktServiceAvailable()) {
      window.roktService
        .getPlacements()
        .then((placements: any) => {
          if (placements?.length) {
            placements.forEach((placement: any) => {
              PLACEMENTS_CALLBACKS.forEach((callback: Callbacks) => {
                placement
                  .on(callback.placementEvent)
                  .subscribe((this as any)[callback.placementCallback]);
              });
            });
          }
        })
        .catch((error: any) => {
          console.error('Error getting placements:', error);
        });
      return;
    }

    console.warn('roktService is not available, cannot get placements');
  }

  private _getCustomerCartData(sponsId?: string, component?: string) {
    const requestInit: RequestInit = {
      method: 'GET',
      mode: 'cors',
      headers: {
        'content-type': 'application/json;charset=UTF-8',
        accept: 'application/json',
      },
    };

    const input = `${API_BASE}/upsell/cart/${sponsId}?components=${component ?? ''}`;
    return fetch(input, requestInit)
      .then((resp) => {
        if (!resp.ok) {
          dispatchException('201');
        } else {
          return resp.json();
        }
      })
      .then((data) => data)
      .catch((error) => {
        throw error;
      });
  }

  private _isResale(inventoryType: string): string {
    const isResale =
      typeof inventoryType === 'string' && inventoryType.toLowerCase() === INVENTORY_TYPE_RESALE;
    return isResale ? 'yes' : 'no';
  }

  getRoktSessionID(upsellCartData: UpsellCartData, component: string | undefined): string {
    if (component === 'roktUpsells' || component === 'insuranceRokt') {
      return upsellCartData.roktUpsells?.sessionId ?? '';
    } else {
      return upsellCartData.parking?.rokt?.sessionId ?? '';
    }
  }

  private _buildRoktInitConfig(
    upsellCartData: UpsellCartData,
    component: string | undefined,
    noDeviceId: boolean,
    pageIdentifier?: string
  ) {
    const sessionId = this.getRoktSessionID(upsellCartData, component);
    const skipInitialSelection = true;
    return {
      pageIdentifier: pageIdentifier ?? PAGE_IDENTIFIER,
      sandbox: JSON.parse(ROKT_SANDBOX_WIDGET_MODE ?? false.toString()),
      skipInitialSelection,
      sessionId,
      noDeviceId,
    };
  }

  private _buildRoktPayload(
    upsellCartData: UpsellCartData,
    attributes: RoktAttributes
  ): roktPayload {
    const { eventDate, orderDetails, userAgent } = upsellCartData || {};

    const resale = this._isResale(orderDetails?.inventoryType);
    return {
      eventDate,
      resale,
      userAgent,
      ...attributes,
    };
  }

  _handlerGA(pageVariantName: string) {
    commService.routeEventMessage({
      data: {
        event: InboundEvents.PARKWHIZ_SDK_ON_UX_VARIANT,
        message: JSON.stringify(pageVariantName),
      },
    });
  }

  private _setGA(): void {
    const { RoktEventType = { PageVariantNameUpdated: '' } } = window.roktService;
    const { PageVariantNameUpdated: variantName } = RoktEventType;
    window.roktService.on(variantName).subscribe(this._handlerGA);
  }

  _iframeBodyMarginsManager(component: string, pageIdentifier: string): void {
    const cssClassName = this._evalMargins(MARGIN_LIST, component, pageIdentifier);

    if (cssClassName) {
      window.document.body.classList.add(cssClassName);
    }
  }

  _evalMargins(marginsList: Margins[], component: string, pageIdentifier: string) {
    const margin = marginsList.find(
      (margin) =>
        margin.pageIdentifier === pageIdentifier &&
        (margin.components.length === 0 || margin.components.includes(component))
    );

    return margin ? margin.className : 'no-margin-body';
  }
}

window.SponsParkwhizWidget = new SponsParkwhizWidget();

export default SponsParkwhizWidget;
